// src/services/campaignDraftService.ts
//
// Handles all CRUD for DraftCampaign records stored in the backend's
// `campaign_draftcampaign` table.
//
// Backend endpoints (all under /campaign/drafts/):
//   GET    /campaign/drafts/                  – list all drafts for current user
//   POST   /campaign/drafts/save/             – create or update a draft (upsert)
//   DELETE /campaign/drafts/<id>/             – delete a specific draft
//   POST   /campaign/drafts/<id>/launch/      – publish draft → real Campaign, then delete draft

import axiosInstance from '../config/axiosInstance';
import { DraftProperty } from './draftService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const DRAFTS_BASE = `${API_BASE_URL}/campaign/drafts`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CampaignDraftPayload {
  campaign_type?: string;
  campaign_name?: string;
  status?: string;
  campaign_subject?: string;
  message?: string;
  html?: string;
  template?: string;
  custom_template_id?: number;
  service_type?: string;
  property_id?: number;
  service_id?: number;
  association_ids?: number[];
  county_ids?: number[];
  agent_user_ids?: number[];
  custom_agent_user_ids?: number[];
  custom_dataset_names?: string[];
  scheduled_date?: string;
  scheduled_timezone?: string;
  recipient_count?: number;
  image_url?: string;
  thumbnail_url?: string;
  is_recurring?: boolean;
  recurrence_type?: string;
  recurring_end_date?: string;
  custom_recurring_dates?: string[];
  payment_intent_id?: string;
  draft_data?: Record<string, any>;
  [key: string]: any;
}

export interface CampaignDraftRecord {
  id: number;
  campaign_type: string;
  campaign_name?: string;
  status?: string;
  campaign_subject?: string;
  message?: string;
  html?: string;
  template?: string;
  custom_template_id?: number;
  service_type?: string;
  property_id?: number;
  service_id?: number;
  association_ids_data?: number[];
  county_ids_data?: number[];
  agent_user_ids_data?: number[];
  custom_agent_user_ids_data?: number[];
  custom_dataset_names?: string[];
  scheduled_date?: string;
  scheduled_timezone?: string;
  recipient_count?: number;
  image_url?: string;
  thumbnail_url?: string;
  is_recurring?: boolean;
  recurrence_type?: string;
  recurring_end_date?: string;
  custom_recurring_dates?: string[];
  payment_intent_id?: string;
  draft_data?: Record<string, any>;
  created?: string;
  updated?: string;
}

export interface SaveDraftResponse {
  success: boolean;
  data?: DraftProperty;
  raw?: CampaignDraftRecord;
  message?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Map a raw DraftCampaign record returned by the backend to the
 * DraftProperty shape the frontend wizard already understands.
 */
function toDraftProperty(record: CampaignDraftRecord): DraftProperty {
  const raw = record as any;
  const source = {
    ...raw,
    ...(raw.draft_data || {}),
  };
  const propertyId = record.property_id ?? source.property_id;
  const serviceId = record.service_id ?? source.service_id;

  return {
    id: record.id,
    campaign_type: record.campaign_type as any,
    campaign_name: source.campaign_name,
    title: source.title || source.campaign_name || source.headline || undefined,
    headline: source.headline || source.campaign_name || undefined,
    listing_id: source.listing_id || source.listingId || undefined,
    address: source.address || undefined,
    postal_code: source.postal_code || source.postalCode || undefined,
    city: source.city || undefined,
    state: source.state || undefined,
    price: source.price || undefined,
    bedrooms: source.bedrooms || undefined,
    bathrooms: source.bathrooms || undefined,
    area: source.area || source.sqft || undefined,
    property_type: source.property_type || source.propertyType || undefined,
    status: source.status || record.status,
    description: source.description || source.message || undefined,
    text_section: source.text_section || source.textSection || undefined,
    subject: source.subject || record.campaign_subject || undefined,
    template: source.template || source.template_name || undefined,
    selected_template_id: record.custom_template_id?.toString() || source.selected_template_id,
    service_type: record.service_type || source.service_type,
    property_id: propertyId,
    service_id: serviceId,
    selected_datasets: record.association_ids_data || source.selected_datasets || [],
    selected_custom_datasets: record.custom_dataset_names || source.selected_custom_datasets || [],
    send_to: source.send_to,
    selected_state: source.selected_state,
    image_url: source.image_url || source.image || source.hero_image || source.heroImage || undefined,
    hero_image: source.hero_image || source.heroImage || undefined,
    photos: source.photos || undefined,
    service_photos: source.service_photos || undefined,
    features: source.features || source.service_features || undefined,
    key_features: source.key_features || undefined,
    property_list_name: source.property_list_name || undefined,
    propertyListType: source.propertyListType || undefined,
    is_open_house: source.is_open_house || undefined,
    scheduled_date: record.scheduled_date,
    scheduled_timezone: record.scheduled_timezone,
    recipient_count: record.recipient_count,
    is_recurring: record.is_recurring,
    recurrence_type: record.recurrence_type as any,
    recurring_end_date: record.recurring_end_date,
    custom_recurring_dates: record.custom_recurring_dates,
    created: record.created,
    updated: record.updated,
    time_since_update: source.time_since_update,
    current_step: source.current_step || source.currentStep || source.step,
  };
}

// ─── Service class ────────────────────────────────────────────────────────────

class CampaignDraftService {

  /**
   * Fetch all DraftCampaign records for the current user.
   */
  async listDrafts(): Promise<CampaignDraftRecord[]> {
    const response = await axiosInstance.get<CampaignDraftRecord[]>(`${DRAFTS_BASE}/`);
    // Backend returns a plain array
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Create or update a draft (upsert keyed on campaign_type).
   * Returns a normalised SaveDraftResponse so call sites can check .success.
   */
  async saveDraft(payload: CampaignDraftPayload): Promise<SaveDraftResponse> {
    try {
      const response = await axiosInstance.post<CampaignDraftRecord>(
        `${DRAFTS_BASE}/save/`,
        payload,
      );
      return { success: true, data: toDraftProperty(response.data), raw: response.data };
    } catch (error: any) {
      console.error('[CampaignDraftService] saveDraft error:', error?.response?.data || error);
      return {
        success: false,
        message: error?.response?.data?.detail || 'Failed to save draft',
      };
    }
  }

  /**
   * Delete a draft by its numeric PK.
   */
  async deleteDraft(draftId: number): Promise<void> {
    await axiosInstance.delete(`${DRAFTS_BASE}/${draftId}/`);
  }

  /**
   * Delete the draft that matches a given campaign_type for the current user.
   * Fetches the list first to resolve the id.
   */
  async deleteDraftByType(campaignType: string): Promise<void> {
    try {
      const drafts = await this.listDrafts();
      const normalised = campaignType.trim().toLowerCase().replace(/-/g, '_');
      const target = drafts.find((d) => d.campaign_type === normalised);
      if (target) {
        await this.deleteDraft(target.id);
      }
    } catch (error) {
      console.warn('[CampaignDraftService] deleteDraftByType error:', error);
    }
  }

  /**
   * Publish a draft: creates a real Campaign and deletes the draft.
   */
  async launchDraft(draftId: number, overrides?: Record<string, any>): Promise<any> {
    const response = await axiosInstance.post(`${DRAFTS_BASE}/${draftId}/launch/`, overrides || {});
    return response.data;
  }

  // ── Convenience getters (match old draftService API used by wizard) ─────────

  async getPropertyDraft(): Promise<DraftProperty | null> {
    try {
      const drafts = await this.listDrafts();
      const record = drafts.find((d) => d.campaign_type === 'property');
      return record ? toDraftProperty(record) : null;
    } catch {
      return null;
    }
  }

  async getServiceDraft(): Promise<DraftProperty | null> {
    try {
      const drafts = await this.listDrafts();
      const record = drafts.find((d) => d.campaign_type === 'service');
      return record ? toDraftProperty(record) : null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch both property & service drafts in parallel.
   * Mirrors the old campaignDraftService.getBothDrafts() API.
   */
  async getBothDrafts(): Promise<{
    propertyDraft: DraftProperty | null;
    serviceDraft: DraftProperty | null;
  }> {
    try {
      const drafts = await this.listDrafts();
      const propertyRecord = drafts.find((d) => d.campaign_type === 'property');
      const serviceRecord = drafts.find((d) => d.campaign_type === 'service');
      return {
        propertyDraft: propertyRecord ? toDraftProperty(propertyRecord) : null,
        serviceDraft: serviceRecord ? toDraftProperty(serviceRecord) : null,
      };
    } catch {
      return { propertyDraft: null, serviceDraft: null };
    }
  }
}

const campaignDraftService = new CampaignDraftService();
export default campaignDraftService;
